/*
 * When and where a JPEG says it was taken: from its EXIF data, or null.
 *
 * Used when a photo is picked from the device's storage (Field Mode's editors
 * and its camera's fallback, the map's observation wizard): a phone usually
 * writes its fix and the moment into the file, and those are better than a
 * pin dropped from memory or the moment the file was picked. They go to
 * POST /photos as the photo's capture tags (0053).
 *
 * The time is read only where its zone is known: DateTimeOriginal with
 * OffsetTimeOriginal, or a bare DateTimeOriginal whose zone the GPS block's UTC
 * time gives (the two agree to a whole zone, within two minutes; the GPS time
 * alone is the fix's, which may be stale, and is never used as the shutter's).
 * Otherwise (many Android phones and older cameras write no offset) it is
 * left out, never guessed at: POST /photos refuses a time with no
 * zone, and the zone the phone is in now need not be the one the photo was
 * taken in. A time the server would refuse anyway (before 2000, or ahead of
 * its clock: photos.py CAPTURE_EARLIEST, CAPTURE_CLOCK_SKEW) is left out too,
 * so the photo is not sent twice over it. The accuracy is the GPS block's
 * GPSHPositioningError, in metres. Nothing else is read -- not the camera or
 * the owner. An OS photo picker may strip the location before the browser sees
 * the file, so none of this can be promised.
 *
 * No library: these are a few rationals and strings, and a parser for every
 * EXIF tag would be a dependency for nine of them. Anything unexpected -- not
 * a JPEG, no EXIF, no GPS, a malformed offset, a 0,0 fix, a date that is not
 * one -- is null, never an error.
 */

const SOI = 0xffd8;
const APP1 = 0xffe1;
const EXIF_IFD_POINTER = 0x8769;
const GPS_IFD_POINTER = 0x8825;
const TAG = {
  // In the GPS IFD.
  latRef: 1, lat: 2, lngRef: 3, lng: 4, gpsTime: 7, gpsDate: 0x1d, accuracy: 0x1f,
  // In the Exif IFD.
  dateTimeOriginal: 0x9003, offsetTimeOriginal: 0x9011,
};
// What POST /photos takes (photos.py): no earlier, no further ahead of its
// clock, and no less sure a fix.
const EARLIEST = Date.UTC(2000, 0, 1);
const CLOCK_SKEW_MS = 10 * 60 * 1000;
const MAX_ACCURACY_M = 100000;

function readIfd(view, tiff, offset, little) {
  const entries = new Map();
  if (offset + 2 > view.byteLength) return entries;
  const count = view.getUint16(offset, little);
  for (let i = 0; i < count; i += 1) {
    const at = offset + 2 + i * 12;
    if (at + 12 > view.byteLength) break;
    entries.set(view.getUint16(at, little), {
      type: view.getUint16(at + 2, little),
      count: view.getUint32(at + 4, little),
      valueAt: at + 8,
    });
  }
  return entries;
}

function asciiAt(view, tiff, entry, little) {
  // Up to 4 bytes sit in the entry itself (N, S, E or W and a NUL); more, such
  // as "2026:10:06 09:12:30", at an offset. Read up to the first NUL.
  if (!entry || !entry.count) return '';
  const at = entry.count <= 4 ? entry.valueAt : tiff + view.getUint32(entry.valueAt, little);
  if (at + entry.count > view.byteLength) return '';
  let text = '';
  for (let i = 0; i < entry.count; i += 1) {
    const code = view.getUint8(at + i);
    if (code === 0) break;
    text += String.fromCharCode(code);
  }
  return text.trim();
}

function rationals(view, tiff, entry, little, n) {
  // `n` unsigned RATIONALs (type 5), always stored at an offset.
  if (!entry || entry.type !== 5 || entry.count !== n) return null;
  const at = tiff + view.getUint32(entry.valueAt, little);
  if (at + n * 8 > view.byteLength) return null;
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const den = view.getUint32(at + i * 8 + 4, little);
    out.push(den ? view.getUint32(at + i * 8, little) / den : NaN);
  }
  return out;
}

function degrees(view, tiff, entry, little) {
  // Degrees, minutes, seconds.
  const p = rationals(view, tiff, entry, little, 3);
  return p ? p[0] + p[1] / 60 + p[2] / 3600 : NaN;
}

/** The TIFF block of a JPEG's EXIF segment, or null. May throw on a malformed file. */
function exifBlock(buffer) {
  const view = new DataView(buffer);
  if (view.byteLength < 4 || view.getUint16(0) !== SOI) return null;
  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    const marker = view.getUint16(offset);
    const length = view.getUint16(offset + 2);
    if (marker === APP1 && view.getUint32(offset + 4) === 0x45786966) { // "Exif"
      const tiff = offset + 10;
      const little = view.getUint16(tiff) === 0x4949; // "II"
      const ifd0 = readIfd(view, tiff, tiff + view.getUint32(tiff + 4, little), little);
      return { view, tiff, little, ifd0 };
    }
    if ((marker & 0xff00) !== 0xff00 || marker === 0xffda) return null; // start of scan: no EXIF ahead
    offset += 2 + length;
  }
  return null;
}

/** The IFD a pointer in IFD0 leads to, or null. */
function subIfd({ view, tiff, little, ifd0 }, pointerTag) {
  const pointer = ifd0.get(pointerTag);
  return pointer ? readIfd(view, tiff, tiff + view.getUint32(pointer.valueAt, little), little) : null;
}

function placeOf({ view, tiff, little }, gps) {
  let lat = degrees(view, tiff, gps.get(TAG.lat), little);
  let lng = degrees(view, tiff, gps.get(TAG.lng), little);
  if (asciiAt(view, tiff, gps.get(TAG.latRef), little) === 'S') lat = -lat;
  if (asciiAt(view, tiff, gps.get(TAG.lngRef), little) === 'W') lng = -lng;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180 || (lat === 0 && lng === 0)) return null;
  return { lat: Number(lat.toFixed(6)), lng: Number(lng.toFixed(6)) };
}

function accuracyOf({ view, tiff, little }, gps) {
  const [metres] = rationals(view, tiff, gps.get(TAG.accuracy), little, 1) || [];
  return Number.isFinite(metres) && metres >= 0 && metres <= MAX_ACCURACY_M ? metres : null;
}

/* Milliseconds since the epoch of a wall-clock reading taken as UTC, or NaN
 * for one that is no moment at all (Date.UTC would roll 31 February over). */
function utcOf(y, mo, d, h, mi, s) {
  const ms = Date.UTC(y, mo - 1, d, h, mi, s);
  const back = new Date(ms);
  return back.getUTCFullYear() === y && back.getUTCMonth() === mo - 1 && back.getUTCDate() === d
    && back.getUTCHours() === h && back.getUTCMinutes() === mi && back.getUTCSeconds() === s ? ms : NaN;
}

const EXIF_MOMENT = /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/;
const EXIF_DAY = /^(\d{4}):(\d{2}):(\d{2})$/;
const OFFSET = /^([+-])(\d{2}):(\d{2})$/;

// Zones come in 15-minute steps, up to 14 hours either way; a camera clock and
// a GPS fix taken together agree to within this much.
const ZONE_STEP_MIN = 15;
const MAX_ZONE_MIN = 14 * 60;
const GPS_AGREE_MS = 2 * 60 * 1000;

/** The moment of the shutter, if the file says it with a zone. */
function momentsOf({ view, tiff, little }, exif, gps) {
  const out = [];
  // DateTimeOriginal is the camera's wall clock, read here as if it were UTC.
  const local = exif && EXIF_MOMENT.exec(asciiAt(view, tiff, exif.get(TAG.dateTimeOriginal), little));
  const wall = local ? utcOf(...local.slice(1).map(Number)) : NaN;
  if (!Number.isFinite(wall)) return out;
  // OffsetTimeOriginal says which zone that clock was on.
  const zone = exif && OFFSET.exec(asciiAt(view, tiff, exif.get(TAG.offsetTimeOriginal), little));
  if (zone && Number(zone[2]) <= 14 && Number(zone[3]) < 60) {
    const east = (zone[1] === '-' ? -1 : 1) * (Number(zone[2]) * 60 + Number(zone[3]));
    out.push(wall - east * 60 * 1000);
    return out;
  }
  // No offset. The GPS block's date and time are UTC, but they are the time of
  // the FIX, which can be a cached one from long before the shutter (indoors,
  // under canopy, a cold receiver). So they only tell the camera clock's zone:
  // the difference must be a whole zone to within GPS_AGREE_MS, and the moment
  // is then the camera's own clock in that zone. Anything else is no time.
  const day = gps && EXIF_DAY.exec(asciiAt(view, tiff, gps.get(TAG.gpsDate), little));
  const time = gps && rationals(view, tiff, gps.get(TAG.gpsTime), little, 3);
  if (day && time && time[2] >= 0 && time[2] < 60) {
    const [y, mo, d] = day.slice(1).map(Number);
    const fix = utcOf(y, mo, d, time[0], time[1], 0) + Math.round(time[2] * 1000);
    const zoneMin = Math.round((wall - fix) / 60000 / ZONE_STEP_MIN) * ZONE_STEP_MIN;
    if (Number.isFinite(fix) && Math.abs(zoneMin) <= MAX_ZONE_MIN
        && Math.abs(wall - fix - zoneMin * 60000) <= GPS_AGREE_MS) {
      out.push(wall - zoneMin * 60000);
    }
  }
  return out;
}

/**
 * {gps: {lat, lng, accuracy_m} | null, takenAt: Date | null} from an
 * ArrayBuffer of a JPEG, or null when it says neither. `now` is the clock a
 * time ahead of is no time.
 */
export function exifFromJpeg(buffer, now = Date.now()) {
  try {
    const block = exifBlock(buffer);
    if (!block) return null;
    const exif = subIfd(block, EXIF_IFD_POINTER);
    const gpsIfd = subIfd(block, GPS_IFD_POINTER);
    const place = gpsIfd ? placeOf(block, gpsIfd) : null;
    const gps = place ? { ...place, accuracy_m: accuracyOf(block, gpsIfd) } : null;
    const at = momentsOf(block, exif, gpsIfd)
      .find(ms => Number.isFinite(ms) && ms >= EARLIEST && ms <= now + CLOCK_SKEW_MS);
    const takenAt = at === undefined ? null : new Date(at);
    return gps || takenAt ? { gps, takenAt } : null;
  } catch {
    return null;
  }
}

/** {lat, lng} from an ArrayBuffer of a JPEG, or null. */
export function gpsFromJpeg(buffer) {
  try {
    const block = exifBlock(buffer);
    const gps = block && subIfd(block, GPS_IFD_POINTER);
    return gps ? placeOf(block, gps) : null;
  } catch {
    return null;
  }
}

/* The first 128 KB of a picked JPEG, where EXIF lives, or null. */
async function headOf(file) {
  if (!file || (file.type && !/jpe?g/i.test(file.type))) return null;
  const head = file.slice ? file.slice(0, 128 * 1024) : file;
  return head.arrayBuffer ? head.arrayBuffer() : new Response(head).arrayBuffer();
}

/** What a picked file says of when and where it was taken (exifFromJpeg), or null. */
export async function exifFromFile(file, now = Date.now()) {
  try {
    const buffer = await headOf(file);
    return buffer ? exifFromJpeg(buffer, now) : null;
  } catch {
    return null;
  }
}

/** The GPS of a picked file, or null. */
export async function gpsFromFile(file) {
  try {
    const buffer = await headOf(file);
    return buffer ? gpsFromJpeg(buffer) : null;
  } catch {
    return null;
  }
}
